import { memo, useCallback, useMemo, useState } from 'react'
import { FormatterCell, GenericColumnModel } from '../models'
import { EditableCell } from '../Body/EditableCell/EditableCell'
import styles from './footer.module.scss'

type Props = {
	column: GenericColumnModel
	columnWidth: number
	getCellValue(column: GenericColumnModel): string
	rowData: any
	cellEdited?: (columnId: string, value: any) => Promise<void>
}

const CellWithoutMemo = ({ column, columnWidth, getCellValue, rowData, cellEdited }: Props) => {
	const [saving, setSaving] = useState(false);

	const cellValue = useMemo(
		() => getCellValue(column),
		[getCellValue, column]
	)

	const formatterCell: FormatterCell = useMemo(
		() => ({
			value: cellValue,
			rowData,
			columnId: column.id
		}),
		[cellValue, rowData, column.id]
	)

	const isCellEditable = useMemo(
		() => column.footerEditable?.(formatterCell) || false,
		[column, formatterCell]
	)

	const onChangeCompleteCallback = useCallback(
		async (columnId: string, value: any) => {
			if (cellEdited && rowData[columnId] !== value) {
				setSaving(true);
				await cellEdited(columnId, value);
				setSaving(false);
			}
		},
		[cellEdited, rowData]
	)

	const className = `${styles.cell} ${isCellEditable ? styles.editing : ''} ${!!column.frozen ? styles.frozen : ''}`;

	return (
		<div
			className={className} style={{ width: `${columnWidth}px` }}
			data-type='cell'
			data-footer='true'
		>
			{isCellEditable &&
				<EditableCell
					column={column}
					value={rowData[column.id]}
					onChangeComplete={onChangeCompleteCallback}
					loading={saving}
					formatterCell={formatterCell}
				/>
			}
			<div
				className={`${styles.content} ${styles[`align_${column.align}`] || ''}`}
				style={{ visibility: isCellEditable ? 'hidden' : 'unset' }}
			>
				<span className={styles.inner} data-columnid={column.id}>
					{cellValue}
				</span>
			</div>
		</div>
	)
}

export const Cell = memo(CellWithoutMemo);
